import { ContextAndMessagesAndSelected, ContextWithQuery, ContextWithTranslatedQuery, defaultDereferenceMessages, DereferenceMessages, SelectorFn, getAccessibleIndices, isAgentAccessible } from "@me8eon/agent";
import { ErrorsOr, isErrors, LogAnd, mapLog } from "@me8eon/errors";
import { AiAndToolOptions, AiClients, AiToolsRepository, callAiAndAnyTools } from "@me8eon/aiclient";
import { safeArray } from "@me8eon/arrays";
import { BaseMessage, Message } from "@me8eon/messages";


export type LlmSelector = {
    type: "llm"
    model: string
    deploymentId?: string
    prompt: BaseMessage[]
    description?: string
    tools?: string[]
    temperature?: number
}




export const llmSelector = <Context extends ContextWithQuery & ContextWithTranslatedQuery, Pipeline>(
        aiClients: AiClients,
        toolRepo: AiToolsRepository,
        deref: DereferenceMessages<any> = defaultDereferenceMessages): SelectorFn<Context, LlmSelector> => ({
        //note no isDefinedAt which defaults to true
        execute: async (selector, context: Context, messages: Message[], { cards }): Promise<LogAnd<ErrorsOr<ContextAndMessagesAndSelected<Context>>>> => {
            const accessibleIndices = getAccessibleIndices();
            const mainAgentEntries = Object.entries(cards.cards).filter(([name, card]) => card.main !== false && isAgentAccessible(card, accessibleIndices));
            const agentPurposes = mainAgentEntries.map(([name, card]) => `${name}: ${card.purpose}`).join("\n");
            const agentNames = mainAgentEntries.map(([name, card]) => name).sort();
            const agentSummary = mainAgentEntries.map(([name, card]) =>
                `Agent Name: **${name}**: \nAgent Purpose: ${card.purpose}. \nSample questions: ${safeArray(card.samples).map(q => `" - ${q}"`).join("\n\n")}`,
            ).join("\n\n---\n");
            const msgs = JSON.stringify(messages);
            const promptMessages = deref({ context, agentNames: agentNames.join(", "), agentPurposes, agentSummary, messages: msgs }, selector.prompt);
            if (isErrors(promptMessages)) return { ...promptMessages, log: { whatHappened: "find.agent.dereference.prefix", severity: "error" } };

            const aiClient = aiClients[selector.model];
            if (!aiClient) return {
                errors: [`llmSelector. No AI client found for model ${selector.model}. Legal models are: ${Object.keys(aiClients).sort().join(", ")}. Selector: ${JSON.stringify(selector)}`],
                log: { whatHappened: "find.agent.no.client", severity: "error", params: { selector } },
            };
            const content = context.translatedQuery || context.query;

            const options: AiAndToolOptions = {
                legalTools: selector.tools || [],
                deploymentId: selector.deploymentId,
                temperature: selector.temperature,
                toolRepo,
            };

            const prompts: Message[] = [...promptMessages.value, { role: "user", content }];
            const response = await callAiAndAnyTools(aiClient, toolRepo, prompts, options);

            return mapLog(response, m => {
                const lastMessage = m[m.length - 1];
                const lastContent = lastMessage.content.trim();
                const value: ContextAndMessagesAndSelected<Context> = { selected: lastContent, context, messages };
                return value;
            });
        },
    })
;
